import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {

  Snackbar,
  Alert,
  AlertColor,
  Button,
  useTheme,
  Box,
  IconButton,
} from '@mui/material';
import Slide, { SlideProps } from '@mui/material/Slide';
import { Close } from '@mui/icons-material';
import { font } from '../../utils/fontHelper';

function SlideUpTransition(props: SlideProps) {
  return <Slide {...props} direction="up" timeout={{ enter: 320, exit: 220 }} />;
}

/** Light surfaces so body text can stay dark grey with WCAG-friendly contrast (works on dark theme UI). */
const TOAST_TEXT = 'rgba(0, 0, 0, 0.87)';
const toastSeveritySx: Record<AlertColor, object> = {
  success: {
    backgroundColor: '#E8F5E9',
    color: TOAST_TEXT,
    '& .MuiAlert-icon': { color: '#2E7D32' },
  },
  error: {
    backgroundColor: '#FFEBEE',
    color: TOAST_TEXT,
    '& .MuiAlert-icon': { color: '#C62828' },
  },
  warning: {
    backgroundColor: '#FFF8E1',
    color: TOAST_TEXT,
    '& .MuiAlert-icon': { color: '#E65100' },
  },
  info: {
    backgroundColor: '#E3F2FD',
    color: TOAST_TEXT,
    '& .MuiAlert-icon': { color: '#1565C0' },
  },
};

interface Toast {
  id: string;
  message: string;
  severity: AlertColor;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
  clickableText?: {
    text: string;
    onClick: () => void;
  };
}

interface ToastContextType {
  showToast: (
    message: string,
    severity?: AlertColor,
    duration?: number,
    action?: { label: string; onClick: () => void },
    clickableText?: { text: string; onClick: () => void }
  ) => void;
  showSuccess: (
    message: string, 
    duration?: number, 
    action?: { label: string; onClick: () => void },
    clickableText?: { text: string; onClick: () => void }
  ) => void;
  showError: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
  showInfo: (
    message: string, 
    duration?: number, 
    action?: { label: string; onClick: () => void },
    clickableText?: { text: string; onClick: () => void }
  ) => void;
  hideToast: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toast, setToast] = useState<Toast | null>(null);
  const theme = useTheme();

  const showToast = useCallback(
    (
      message: string,
      severity: AlertColor = 'success',
      duration: number = 3500,
      action?: { label: string; onClick: () => void },
      clickableText?: { text: string; onClick: () => void }
    ) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      setToast({
        id,
        message,
        severity,
        duration,
        action,
        clickableText,
      });
    },
    []
  );

  const showSuccess = useCallback(
    (
      message: string,
      duration: number = 3500,
      action?: { label: string; onClick: () => void },
      clickableText?: { text: string; onClick: () => void }
    ) => {
      showToast(message, 'success', duration, action, clickableText);
    },
    [showToast]
  );

  const showError = useCallback(
    (message: string, duration: number = 5000) => {
      showToast(message, 'error', duration);
    },
    [showToast]
  );

  const showWarning = useCallback(
    (message: string, duration: number = 5000) => {
      showToast(message, 'warning', duration);
    },
    [showToast]
  );

  const showInfo = useCallback(
    (
      message: string,
      duration: number = 3500,
      action?: { label: string; onClick: () => void },
      clickableText?: { text: string; onClick: () => void }
    ) => {
      showToast(message, 'info', duration, action, clickableText);
    },
    [showToast]
  );

  const hideToast = useCallback(() => {
    setToast(null);
  }, []);

  const handleClose = useCallback(
    (event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway') {
        return;
      }
      hideToast();
    },
    [hideToast]
  );

  const contextValue = useMemo<ToastContextType>(
    () => ({
      showToast,
      showSuccess,
      showError,
      showWarning,
      showInfo,
      hideToast,
    }),
    [showToast, showSuccess, showError, showWarning, showInfo, hideToast]
  );

  const snackbarNode = (
      <Snackbar
        key={toast?.id ?? 'snackbar-closed'}
        open={!!toast}
        autoHideDuration={toast?.duration ?? 3500}
        onClose={handleClose}
        TransitionComponent={SlideUpTransition}
        transitionDuration={{ enter: 320, exit: 220 }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          // Above bottom nav / modals; avoid clipping from app overflow/stacking contexts
          zIndex: 99999,
          pointerEvents: 'auto',
          // Account for iOS safe area inset + bottom nav height
          bottom: {
            xs: 'calc(var(--safe-area-inset-bottom) + 90px)',
            sm: 'calc(var(--safe-area-inset-bottom) + 90px)',
            md: 24
          },
          maxWidth: { xs: 'calc(100vw - 32px)', sm: 560 },
        }}
      >
        <Alert 
          variant="standard"
          severity={toast?.severity ?? 'success'}
          action={
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
              {toast?.action && (
                <Button
                  size="medium"
                  onClick={() => {
                    toast.action?.onClick();
                    hideToast();
                  }}
                  sx={{
                    color: 'inherit',
                    fontWeight: 700,
                    textTransform: 'none',
                    minWidth: 'auto',
                    px: 1.25,
                    fontSize: 15,
                    fontFamily: font(),
                  }}
                >
                  {toast.action.label}
                </Button>
              )}
              <IconButton
                size="small"
                onClick={hideToast}
                sx={{ color: 'inherit' }}
                aria-label="Dismiss notification"
              >
                <Close sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          }
          sx={{
            width: '100%',
            minHeight: 52,
            borderRadius: 3,
            boxShadow: theme.shadows[8],
            alignItems: 'center',
            border: 'none',
            ...toastSeveritySx[toast?.severity ?? 'success'],
            '& .MuiAlert-message': {
              py: 1,
              pr: 0.5,
              fontSize: 16,
              lineHeight: 1.4,
              fontWeight: 500,
              fontFamily: font(),
              color: TOAST_TEXT,
            },
            '& .MuiAlert-icon': {
              fontSize: 26,
              alignSelf: 'center',
              opacity: 1,
            },
          }}
        >
          {toast?.clickableText ? (
            <Box component="span">
              {toast.message.split(toast.clickableText.text).map((part, index, array) => (
                <React.Fragment key={index}>
                  {part}
                  {index < array.length - 1 && toast.clickableText && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={toast.clickableText.onClick}
                      sx={{
                        minWidth: 'auto',
                        p: 0,
                        textTransform: 'none',
                        textDecoration: 'underline',
                        color: 'inherit',
                        fontWeight: 600,
                        fontSize: 'inherit',
                        fontFamily: 'inherit',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {toast.clickableText.text}
                    </Button>
                  )}
                </React.Fragment>
              ))}
            </Box>
          ) : (
            toast?.message
          )}
        </Alert>
      </Snackbar>
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {typeof document !== 'undefined' ? createPortal(snackbarNode, document.body) : null}
    </ToastContext.Provider>
  );
};

export default ToastProvider; 